import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { persistCache } from '@scio3/graphql-client';
import type { InMemoryCache } from '@apollo/client';

const PERSIST_DEBOUNCE_MS = 2_000;
const PERSIST_INTERVAL_MS = 30_000;

/**
 * Auto-persists Apollo cache to IndexedDB.
 *
 * Persist triggers:
 * - Periodic interval (every 30s while tab is active)
 * - Immediate on visibilitychange → hidden (user leaving tab)
 * - On Apollo cache clear (logout)
 *
 * Mount this once inside ApolloProvider (e.g. in App.tsx).
 */
export function useCachePersistence() {
  const client = useApolloClient();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const cache = client.cache as InMemoryCache;

    function debouncedPersist() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        persistCache(cache);
      }, PERSIST_DEBOUNCE_MS);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        // User leaving tab — persist immediately
        if (timerRef.current) clearTimeout(timerRef.current);
        persistCache(cache);
      }
    }

    function handleBeforeUnload() {
      // Safety net: attempt persist during page teardown (reload/close).
      // visibilitychange → hidden also fires, but the async IndexedDB write
      // may not complete before teardown. Having both handlers gives the
      // browser two chances to flush the cache.
      if (timerRef.current) clearTimeout(timerRef.current);
      persistCache(cache);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Periodic persist while tab is active
    const interval = setInterval(debouncedPersist, PERSIST_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [client]);
}
